import React, { useState, useEffect } from 'react';
import { Badge, IconButton, Box, keyframes } from '@mui/material';
import { Analytics as AnalyticsIcon } from '@mui/icons-material';
import AnalyzerDrawer from './AnalyzerDrawer';

// Create pulse animation using MUI keyframes
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const AnalyzerNotificationCounter = ({ userId }) => {
    const [analyzerCount, setAnalyzerCount] = useState(0);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [analyzers, setAnalyzers] = useState([]);
    const [hasAutoOpened, setHasAutoOpened] = useState(false);

    useEffect(() => {
        // Initialize WebSocket connection for analyzer updates
        if (window.Echo && userId) {
            window.Echo.channel(`public.analyzers.${userId}`)
                .listen('analyzer-received', (e) => {
                    handleNewAnalyzer(e);
                });
        }
    }, [userId]);

    const handleNewAnalyzer = (data) => {
        const analyzerData = {
            event_id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            ...data.data,
            receivedAt: new Date().toISOString()
        };

        setAnalyzers(prev => [analyzerData, ...prev]);
        setAnalyzerCount(prev => prev + 1);

        // Auto-open drawer only the first time until the user interacts with it.
        setHasAutoOpened(prev => {
            if (!prev) {
                setIsDrawerOpen(true);
                return true;
            }
            return prev;
        });

        // Play audio notification if enabled
        if ((localStorage.getItem('audioNotifications') || 'enabled') === 'enabled') {
            const audio = document.getElementById('dingSound');
            if (audio) {
                audio.play().catch(e => console.log('Could not play notification sound'));
            }
        }

        // Trigger table refresh
        if (window.refreshAnalyzerTable) {
            window.refreshAnalyzerTable();
        }
    };

    const handleOpenDrawer = () => {
        setIsDrawerOpen(true);
        setHasAutoOpened(true); // Prevent future auto-opens after manual interaction
    };

    const handleCloseDrawer = () => {
        setIsDrawerOpen(false);
        setHasAutoOpened(true); // Remember the user dismissed the auto-open drawer
    };

    const handleDismissAnalyzer = (analyzerId) => {
        setAnalyzers(prev => prev.filter(analyzer => analyzer.id !== analyzerId));
        setAnalyzerCount(prev => Math.max(0, prev - 1));
    };

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}>
            <IconButton
                onClick={handleOpenDrawer}
                sx={{
                    color: 'text.secondary',
                    '&:hover': {
                        color: 'primary.main'
                    }
                }}
                size="small"
                title="Realtime alerts details"
            >
                <Badge
                    badgeContent={analyzerCount > 99 ? '99+' : analyzerCount}
                    color="error"
                    sx={{
                        '& .MuiBadge-badge': {
                            display: analyzerCount === 0 ? 'none' : 'flex',
                            animation: analyzerCount > 0 ? `${pulse} 2s infinite` : 'none',
                            fontSize: '0.7rem',
                            minWidth: '20px',
                            height: '20px'
                        }
                    }}
                >
                    <AnalyticsIcon fontSize="small" />
                </Badge>
            </IconButton>
            <Box
                component="span"
                role="button"
                tabIndex={0}
                onClick={handleOpenDrawer}
                onKeyUp={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        handleOpenDrawer();
                    }
                }}
                sx={{
                    ml: 0.5,
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                    display: { xs: 'none', md: 'inline' },
                    cursor: 'pointer',
                    '&:hover': {
                        color: 'primary.main'
                    }
                }}
            >
                Realtime alerts details
            </Box>

            <AnalyzerDrawer
                isOpen={isDrawerOpen}
                onClose={handleCloseDrawer}
                analyzers={analyzers}
                onDismiss={handleDismissAnalyzer}
            />
        </Box>
    );
};

export default AnalyzerNotificationCounter;
